<template>
  <div
    :class="{
      'sm:tw-w-full md:tw-w-3/5 ': resourceType !== 'teaching-resource',
      'sm:tw-w-[48%] lg:tw-w-full': resourceType === 'teaching-resource',
    }"
  >
    <div
      :class="{
        dropdown: true,
        show: isOpen,
      }"
    >
      <button
        ref="dropdown-toggle"
        :form="formId"
        class="button button-primary tw-rounded"
        :class="{
          'sm:tw-w-60 md:tw-w-10/12': resourceType !== 'teaching-resource',
          '!tw-mr-0 tw-w-full tw-text-xs sm:tw-text-sm md:tw-text-base':
            resourceType === 'teaching-resource',
        }"
        aria-haspopup="true"
        :aria-expanded="isOpen"
        data-testid="download-dropdown-toggle"
        :data-fill="fill"
        :disabled="isDownloading"
        :aria-disabled="isDownloading"
        :data-has-dropdown="true"
        @click.prevent="toggleOpen()"
      >
        <template v-if="isDownloading">Downloading...</template>
        <template v-else>Download</template>
        <svg
          :class="[
            'icon icon-small !tw-ml-2',
            {
              'tw-pt-0.5': resourceType === 'teaching-resource',
              'tw-pt-1': resourceType === '!teaching-resource',
            },
          ]"
          role="img"
          aria-hidden="true"
          aria-label="Download"
        >
          <use xlink:href="/images/fa-icons.svg#arrow-down-filled"></use>
        </svg>
      </button>
      <div
        ref="dropdown-content"
        :class="[
          'dropdown-content -tw-mt-6 tw-w-full',
          { 'tw-w-full': resourceType === 'teaching-resource' },
        ]"
      >
        <download-link
          :files="resource.files"
          :resource-type="resource.type"
          :event-params="resource.event_params"
          :page-count="resource.page_count"
          :custom-text="customText"
          position="Content | Main CTA"
          click-type="Button Drop-down"
          click-text="Download"
          :resource-country="resource.country"
          :resource-availability="isFree"
          @toggleDownloadButton="updateDownloadButton"
        />
      </div>
    </div>
    <div v-if="error" class="callout callout-error">{{ error }}</div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DownloadLink from '~/components/resource/DownloadLink.vue';
import { isUnitOrLessonPlan } from '~/utils/resource';
import { doesUserHaveQuota, hasUserReachedDownloadLimit } from '~/utils/user';

export default {
  name: 'DownloadButton',
  components: {
    DownloadLink,
  },
  props: {
    files: {
      type: Array,
      default: () => {
        return [];
      },
    },
    resourceType: { type: String, default: 'teaching-resource' },
    isFree: { type: Boolean, default: false },
    pageCount: { type: Number, default: 0 },
    eventParams: {
      type: Object,
      default: () => {
        return {};
      },
    },
    preventDropdown: { type: Boolean, default: false },
    wideDownloadButton: { type: Boolean, default: false },
    formId: { type: String, default: 'downloadResource' },
    buttonPosition: { type: String, default: 'Content | Main CTA' },
    // Slightly different from wideDownloadButton as it only affects the width, not the position as well
    fill: {
      type: Boolean,
      default: false,
    },
    customText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isDownloading: false,
      error: null,
      isOpen: false,
    };
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'loggedInUser', 'resource', 'location']),
    showDropdown() {
      return this.files && this.files.length > 1 && !this.preventDropdown;
    },
  },
  mounted() {
    window.addEventListener('click', this.close);
  },
  beforeDestroy() {
    window.removeEventListener('click', this.close);
  },
  methods: {
    close(e) {
      if (process.browser && this.$refs['dropdown-content']) {
        // close dropdown when clicked outside
        if (
          this.$refs['dropdown-toggle'] !== e.target &&
          !this.$refs['dropdown-content'].contains(e.target)
        ) {
          this.onClose();
        }
      }
    },
    toggleOpen() {
      let pageType = this.resource.type
        .trim()
        .replace(/[\s-]+/g, '_')
        .toLowerCase();
      let productAreas = '';
      if (pageType === 'teaching_resource') {
        pageType = 'resource';
        productAreas = 'resources';
      } else if (pageType === 'unit_plan') {
        pageType = 'unit_plan';
        productAreas = 'unit_plan';
      } else if (pageType === 'resource_pack') {
        pageType = 'pack';
        productAreas = 'pack';
      } else if (pageType === 'widget') {
        productAreas = 'widget';
      } else if (pageType === 'lesson_plan') {
        pageType = 'lesson_plan';
        productAreas = 'lesson_plan';
      }
      this.$mzAnalyticsApi({
        event: 'click',
        name: pageType + '.userclicked',
        productArea: productAreas,
        entityType: pageType,
        entityId: this.resource.id,
      });
      this.isOpen = !this.isOpen;
    },
    onClose() {
      this.isOpen = false;
    },
    toggleUpgradeModal() {
      if (
        doesUserHaveQuota(this.loggedInUser) &&
        this.loggedInUser.subscription?.code !== 'plus_lite'
      ) {
        this.$store.dispatch('modals/show', 'downloadLimitAlert');
        return;
      }

      this.$store.dispatch('modals/show', 'signUpCombined');
    },
    updateDownloadButton(downloadButtonStatus) {
      this.isDownloading = downloadButtonStatus;
    },
    eventUpgradeClicked() {
      const eventType = this.resource.event_params.Resource_Type_Name ? 'Resource' : 'Content';
      const upgradeParams = {
        Click_Text: 'Upgrade to download',
        Click_Type: 'Button',
        Click_Action: 'Send user to page | Plans and Pricing',
        Click_Page_Position: 'Content | Main CTA',
      };
    },
    eventFileDownloaded(file) {
      if (!this.eventParams) {
        return;
      }

      const eventType = this.resource.event_params.Resource_Type_Name ? 'Resource' : 'Content';
      const eventAction =
        file.format === 'slide' || file.format === 'sheet' ? 'Opened' : 'Downloaded'; // Exception for Google Slides or Sheets

      const eventParams = {
        Click_Type: 'Button',
        Click_Action: 'Download | Resource File',
        Click_Page_Position: this.buttonPosition,
        Click_Text: 'Download',
        User_Download_Quota_Left: this.loggedInUser.subscription?.quota_left || undefined,
        Freemium: this.loggedInUser.role === 'free' && !this.resource.is_free,
      };

      const fileParams = {
        File_Customised: false,
        File_Format: file.format,
        File_Id: file.id,
        File_Name: file.name,
      };

      const fileParamsIntercom = {
        fileExtension: file.format,
        downloadType: 'Standard Download',
        file_id: file.id,
        file_name: file.name,
      };

      if (process.client && typeof window.gtag !== 'undefined') {
        window.gtag('event', 'resourceInteraction', {
          userUuid: this.loggedInUser.uuid,
          eventAction: 'Resource Interaction',
          resourceAvailability: this.resource.is_free ? 'Free' : ' Premium',
          resourceName: this.resource.name,
          resourceId: this.resource.file_id,
        });
      }

      const fileDownloadIntercom = {
        ...this.resource.event_params_intercom,
        ...fileParamsIntercom,
        action_type_name: 'Download',
      };

      if (typeof this.$intercom !== 'undefined') {
        this.$intercom.trackEvent('Resource Downloaded', fileDownloadIntercom);
      }
    },
    handleUpgradeToDownloadClick() {
      this.eventUpgradeClicked();

      if (!this.loggedInUser) {
        this.$store.dispatch('modals/show', 'signUpCombined');
        return;
      }
      if (!this.loggedInUser && !doesUserHaveQuota(this.loggedInUser)) {
        this.$router.push(this.localePath('/plans/'));
        return;
      }

      if (isUnitOrLessonPlan(this.resourceType)) {
        this.$store.dispatch('modals/show', 'unitPlanUpgrade');
        return;
      }
      if (this.files && this.files[0] && this.files[0].downloadable_file_type === 'editable') {
        this.$store.dispatch('modals/show', 'customizableResource');
        return;
      }
      if (hasUserReachedDownloadLimit(this.loggedInUser)) {
        this.$store.dispatch('modals/show', 'exhaustedDownloads');
        return;
      }
      if (!this.resource.is_free) {
        this.$store.dispatch('modals/show', 'premiumResource');
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~/assets/scss/components/_filter.scss';
@import '~/assets/scss/components/_dropdown.scss';

.dropdown-content {
  font-size: $font-size-300;
  min-width: 18.75rem;

  a {
    align-items: center;
    display: flex;
  }
}

.button.wide-download-btn {
  display: block;
  margin: 0;
  position: absolute;
  bottom: $base-padding-half;
  left: 0.5%;
  width: 99%;
}

.button[data-fill] {
  width: 100%;
}

.button[data-fill][data-has-dropdown] {
  width: calc(100% - 1.6rem);
}

.callout,
.row {
  margin-top: 1rem;
}
</style>
